import React, { useState, useEffect } from 'react';
import taskDB from '../config/taskDatabaseAPI';
import clientDB from '../config/clientDatabase';
import './TaskManager.css';

const TaskManager = ({ user, company }) => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('dueDate'); // dueDate, priority, createdAt
  const [activeView, setActiveView] = useState('tasks'); // 'tasks' or 'email-inbox'
  const [expandedTasks, setExpandedTasks] = useState({});

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    dueDate: '',
    tags: [],
    priority: 'medium',
    clientId: null
  });

  // Load tasks and stats
  useEffect(() => {
    loadTasks();
  }, []);

  // Filter and sort tasks whenever filters change
  useEffect(() => {
    filterAndSortTasks();
  }, [tasks, filterStatus, filterTag, searchQuery, sortBy]);

  const loadTasks = async () => {
    try {
      const allTasks = await taskDB.getAllTasks();
      const statistics = taskDB.getStatistics();
      setTasks(allTasks);
      setStats(statistics);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const filterAndSortTasks = () => {
    let filtered = [...tasks];

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(task => task.status === filterStatus);
    }

    // Filter by tag
    if (filterTag !== 'all') {
      filtered = filtered.filter(task => task.tags.includes(filterTag));
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(query) ||
        task.description.toLowerCase().includes(query) ||
        task.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Sort tasks
    filtered.sort((a, b) => {
      if (sortBy === 'dueDate') {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      } else if (sortBy === 'priority') {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      } else if (sortBy === 'createdAt') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return 0;
    });

    setFilteredTasks(filtered);
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      alert('Please enter a task title');
      return;
    }

    try {
      await taskDB.createTask(newTask);
      setShowAddModal(false);
      setNewTask({
        title: '',
        description: '',
        dueDate: '',
        tags: [],
        priority: 'medium',
        clientId: null
      });
      await loadTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task. Please try again.');
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask.title.trim()) {
      alert('Please enter a task title');
      return;
    }

    try {
      await taskDB.updateTask(editingTask.id, editingTask);
      setEditingTask(null);
      await loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task. Please try again.');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await taskDB.completeTask(taskId);
      await loadTasks();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleStartTask = async (taskId) => {
    try {
      await taskDB.startTask(taskId);
      await loadTasks();
    } catch (error) {
      console.error('Error starting task:', error);
    }
  };

  const handleReopenTask = async (taskId) => {
    try {
      await taskDB.reopenTask(taskId);
      await loadTasks();
    } catch (error) {
      console.error('Error reopening task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await taskDB.deleteTask(taskId);
        await loadTasks();
      } catch (error) {
        console.error('Error deleting task:', error);
        alert('Failed to delete task. Please try again.');
      }
    }
  };

  const toggleTag = (tag, taskObj) => {
    const currentTags = taskObj.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];

    if (editingTask) {
      setEditingTask({ ...editingTask, tags: newTags });
    } else {
      setNewTask({ ...newTask, tags: newTags });
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'in-progress': return 'status-in-progress';
      case 'todo': return 'status-todo';
      default: return 'status-todo';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  const isOverdue = (task) => {
    if (!task.dueDate || task.status === 'completed') return false;
    return new Date(task.dueDate) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const allTags = taskDB.getAllTags();
  const allClients = clientDB.getAllCompanies().filter(c => c.type === 'client');

  // Get email tasks for inbox view
  const emailTasks = tasks.filter(t => t.emailReference);
  const unassignedEmails = emailTasks.filter(t => !t.clientId);

  const handleAssignToClient = async (taskId, clientId) => {
    try {
      await taskDB.updateTask(taskId, { clientId });
      await loadTasks();
    } catch (error) {
      console.error('Error assigning task to client:', error);
      alert('Failed to assign task. Please try again.');
    }
  };

  const toggleTaskExpanded = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  return (
    <div className="task-manager">
      <div className="task-header">
        <div className="task-title-section">
          <h1>My Tasks</h1>
          <p className="task-subtitle">Personal task management and work tracking</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${activeView === 'tasks' ? 'active' : ''}`}
              onClick={() => setActiveView('tasks')}
            >
              All Tasks
            </button>
            <button
              className={`toggle-btn ${activeView === 'email-inbox' ? 'active' : ''}`}
              onClick={() => setActiveView('email-inbox')}
            >
              📧 Email Inbox {unassignedEmails.length > 0 && <span className="badge-count">{unassignedEmails.length}</span>}
            </button>
          </div>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            + New Task
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="task-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total || 0}</div>
          <div className="stat-label">Total Tasks</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.todo || 0}</div>
          <div className="stat-label">To Do</div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-value">{stats.inProgress || 0}</div>
          <div className="stat-label">In Progress</div>
        </div>
        <div className="stat-card stat-success">
          <div className="stat-value">{stats.completed || 0}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card stat-danger">
          <div className="stat-value">{stats.overdue || 0}</div>
          <div className="stat-label">Overdue</div>
        </div>
        <div className="stat-card stat-info">
          <div className="stat-value">{stats.dueSoon || 0}</div>
          <div className="stat-label">Due Soon</div>
        </div>
      </div>

      {/* Conditional Views */}
      {activeView === 'tasks' ? (
        <>
          {/* Filters and Search */}
          <div className="task-filters">
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="todo">To Do</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Tag</label>
              <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
                <option value="all">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="dueDate">Due Date</option>
                <option value="priority">Priority</option>
                <option value="createdAt">Created Date</option>
              </select>
            </div>
          </div>

          {/* Task List */}
          <div className="task-list">
        {filteredTasks.length === 0 ? (
          <div className="empty-state">
            <h3>No tasks found</h3>
            <p>Create your first task to get started!</p>
          </div>
        ) : (
          filteredTasks.map(task => (
            <div
              key={task.id}
              className={`task-card ${task.status} ${isOverdue(task) ? 'overdue' : ''}`}
            >
              <div className="task-main">
                <div className="task-checkbox">
                  {task.status === 'completed' ? (
                    <button
                      className="checkbox checked"
                      onClick={() => handleReopenTask(task.id)}
                      title="Reopen task"
                    >
                      ✓
                    </button>
                  ) : (
                    <button
                      className="checkbox"
                      onClick={() => handleCompleteTask(task.id)}
                      title="Mark as complete"
                    />
                  )}
                </div>

                <div className="task-content">
                  <div className="task-header-row">
                    <h3 className="task-title">{task.title}</h3>
                    <div className="task-badges">
                      <span className={`badge ${getStatusBadgeClass(task.status)}`}>
                        {task.status.replace('-', ' ')}
                      </span>
                      <span className={`badge ${getPriorityBadgeClass(task.priority)}`}>
                        {task.priority}
                      </span>
                      {isOverdue(task) && (
                        <span className="badge badge-overdue">Overdue</span>
                      )}
                    </div>
                  </div>

                  {task.emailReference ? (
                    /* Email Task - Show email metadata */
                    <div className="email-task-details">
                      <div className="email-metadata">
                        <div className="email-meta-row">
                          <strong>From:</strong> {task.emailReference.from}
                        </div>
                        <div className="email-meta-row">
                          <strong>Subject:</strong> {task.emailReference.subject || task.title}
                        </div>
                        <div className="email-meta-row">
                          <strong>Date:</strong> {new Date(task.emailReference.receivedAt || task.createdAt).toLocaleString()}
                        </div>
                        {task.emailReference.to && (
                          <div className="email-meta-row">
                            <strong>To:</strong> {task.emailReference.to}
                          </div>
                        )}
                      </div>
                      {task.emailReference.body && (
                        <div className="email-body-toggle">
                          <button
                            className="toggle-body-btn"
                            onClick={() => toggleTaskExpanded(task.id)}
                          >
                            {expandedTasks[task.id] ? '▼ Hide Email' : '▶ Show Email'}
                          </button>
                          {expandedTasks[task.id] && (
                            <div className="email-body-expanded">
                              {task.emailReference.body}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Regular Task - Show description */
                    task.description && (
                      <p className="task-description">{task.description}</p>
                    )
                  )}

                  <div className="task-meta">
                    <span className="task-date">
                      📅 {formatDate(task.dueDate)}
                    </span>
                    {task.tags.length > 0 && (
                      <div className="task-tags">
                        {task.tags.map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    {task.clientId && (
                      <span className="task-client">
                        {clientDB.getCompany(task.clientId)?.name}
                      </span>
                    )}
                    {task.emailReference && (
                      <span className="email-indicator">
                        📧 Email Task
                      </span>
                    )}
                  </div>
                </div>

                <div className="task-actions">
                  {task.status === 'todo' && (
                    <button
                      className="btn-action btn-start"
                      onClick={() => handleStartTask(task.id)}
                      title="Start task"
                    >
                      ▶
                    </button>
                  )}
                  <button
                    className="btn-action btn-edit"
                    onClick={() => setEditingTask(task)}
                    title="Edit task"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-action btn-delete"
                    onClick={() => handleDeleteTask(task.id)}
                    title="Delete task"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
          </div>
        </>
      ) : (
        /* Email Inbox View */
        <div className="email-inbox">
          <div className="inbox-header">
            <h2>📧 Email Inbox</h2>
            <p className="inbox-subtitle">
              {unassignedEmails.length > 0
                ? `${unassignedEmails.length} email${unassignedEmails.length !== 1 ? 's' : ''} awaiting client assignment`
                : 'All emails have been assigned'}
            </p>
          </div>

          {/* Unassigned Emails Section */}
          {unassignedEmails.length > 0 && (
            <div className="inbox-section">
              <h3 className="section-title">⚠️ Needs Assignment ({unassignedEmails.length})</h3>
              <div className="email-list">
                {unassignedEmails.map(task => (
                  <div key={task.id} className="email-card unassigned">
                    <div className="email-header-section">
                      <div className="email-icon">📧</div>
                      <div className="email-info">
                        <div className="email-from">
                          <strong>From:</strong> {task.emailReference?.from || 'Unknown'}
                        </div>
                        <div className="email-subject">
                          <strong>Subject:</strong> {task.emailReference?.subject || task.title}
                        </div>
                        <div className="email-date">
                          Received: {new Date(task.emailReference?.receivedAt || task.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {task.description && (
                      <div className="email-preview">
                        {task.description.substring(0, 200)}
                        {task.description.length > 200 && '...'}
                      </div>
                    )}

                    <div className="email-actions">
                      <div className="assign-client">
                        <label>Assign to Client:</label>
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              handleAssignToClient(task.id, e.target.value);
                            }
                          }}
                          className="client-selector"
                        >
                          <option value="">Select a client...</option>
                          {allClients.map(client => (
                            <option key={client.id} value={client.id}>
                              {client.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        className="btn-action btn-edit"
                        onClick={() => setEditingTask(task)}
                        title="Edit task"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-action btn-delete"
                        onClick={() => handleDeleteTask(task.id)}
                        title="Delete email"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {emailTasks.length === 0 && (
            <div className="empty-state">
              <h3>📭 No emails yet</h3>
              <p>Forward emails to your Zapier mailbox and they'll appear here</p>
              <p className="hint">Once emails arrive, you can assign them to clients and track them as tasks</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Task Modal */}
      {(showAddModal || editingTask) && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setEditingTask(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTask ? 'Edit Task' : 'New Task'}</h2>
              <button
                className="close-button"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingTask(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Task Title *</label>
                <input
                  type="text"
                  value={editingTask ? editingTask.title : newTask.title}
                  onChange={(e) => editingTask
                    ? setEditingTask({ ...editingTask, title: e.target.value })
                    : setNewTask({ ...newTask, title: e.target.value })
                  }
                  placeholder="Enter task title"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editingTask ? editingTask.description : newTask.description}
                  onChange={(e) => editingTask
                    ? setEditingTask({ ...editingTask, description: e.target.value })
                    : setNewTask({ ...newTask, description: e.target.value })
                  }
                  placeholder="Add task details..."
                  rows="4"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={editingTask ? editingTask.dueDate : newTask.dueDate}
                    onChange={(e) => editingTask
                      ? setEditingTask({ ...editingTask, dueDate: e.target.value })
                      : setNewTask({ ...newTask, dueDate: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={editingTask ? editingTask.priority : newTask.priority}
                    onChange={(e) => editingTask
                      ? setEditingTask({ ...editingTask, priority: e.target.value })
                      : setNewTask({ ...newTask, priority: e.target.value })
                    }
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Client (Optional)</label>
                <select
                  value={editingTask ? editingTask.clientId || '' : newTask.clientId || ''}
                  onChange={(e) => editingTask
                    ? setEditingTask({ ...editingTask, clientId: e.target.value || null })
                    : setNewTask({ ...newTask, clientId: e.target.value || null })
                  }
                >
                  <option value="">No client</option>
                  {allClients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tags</label>
                <div className="tag-selector">
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      className={`tag-button ${(editingTask ? editingTask.tags : newTask.tags)?.includes(tag) ? 'selected' : ''}`}
                      onClick={() => toggleTag(tag, editingTask || newTask)}
                      type="button"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingTask(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={editingTask ? handleUpdateTask : handleCreateTask}
              >
                {editingTask ? 'Update Task' : 'Create Task'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManager;
